/**
 * Katana Questing API - Krates (XP quests) metadata.
 *
 * External API (app.katana.network ecosystem), not part of the perps exchange API.
 * Allowed in CSP connect-src, see `server/cspConfig.js`.
 * NOTE: The API must also allow our origins via CORS, otherwise requests
 * are blocked by the browser and consumers should fall back to static copy.
 */

import { QUESTS_KRATES_METADATA_URL } from '#/config/constants';

// Reward tiers - returned by the API but not used by us (yet)
// type QuestsKrateTier = {
//   /** E.g. 'COMMON' | 'UNCOMMON' | 'RARE' | 'SUPER_RARE' */
//   rarity: string;
//   probability: number;
//   count: number;
//   rewardAvKatMin: number;
//   rewardAvKatMax: number;
//   rewardXpMin: number;
//   rewardXpMax: number;
//   rewardUsdcMin: number;
//   rewardUsdcMax: number;
// };

export type QuestsKratesMetadata = {
  /** XP price to redeem (open) one Krate */
  priceXp: number;

  // Returned by the API but not used by us (yet):
  // /** ISO date, e.g. '2026-08-01' */
  // monthStart: string;
  // totalSupply: number;
  // maxPurchase: number;
  // supplyExhausted: boolean;
  // tiers: QuestsKrateTier[];
};

/**
 * Runtime validation - external API we do not control, so make sure the
 * response has the structure we rely on before using it.
 */
const isQuestsKratesMetadata = (
  value: unknown
): value is QuestsKratesMetadata =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as QuestsKratesMetadata).priceXp === 'number' &&
  Number.isFinite((value as QuestsKratesMetadata).priceXp);

export const fetchQuestsKratesMetadata =
  async (): Promise<QuestsKratesMetadata> => {
    // Keep this a bare GET without custom headers: adding e.g.
    // `Content-Type: application/json` turns it into a preflighted request,
    // and the questing API does not handle OPTIONS (404) - a simple request
    // avoids the preflight round-trip entirely.
    const response = await fetch(QUESTS_KRATES_METADATA_URL);
    if (!response.ok) {
      throw new Error(`Failed to load Krates metadata (${response.status})`);
    }
    const metadata: unknown = await response.json();
    if (!isQuestsKratesMetadata(metadata)) {
      throw new Error('Unexpected Krates metadata response structure');
    }
    return metadata;
  };
